/**
 * Uma forma de pagamento aplicada → um item de `FormasDePagamento[]` do retrato
 * enviado ao ERP (`CheckoutFaturarNFCe.FormasDePagamento_FormasDePagamentoItem`,
 * YAML linha 1555).
 *
 * Extraído de `pagamentoSlice.montarPagamentosParaPayload` ao implementar a
 * feature 014, e não duplicado nela, por causa da invariante I5: o retrato que
 * o gate valida e o retrato que o faturamento emite precisam ser **byte a byte**
 * o mesmo objeto para os mesmos pagamentos. Duas versões deste mapeamento
 * divergiriam no primeiro campo novo de TEF/PIX, e a divergência apareceria
 * como "o ERP aprovou e depois recusou a mesma venda" — o modo de falha mais
 * grave da 014 (`specs/014-validacao-previa-nfce/data-model.md`, I5).
 *
 * Domínio puro (Constitution II): sem store, sem rede, sem React.
 */

import { reaisDeCentavos } from '../precificacao/dinheiro';
import type { FormaDePagamentoRetrato } from '../venda/montarRetratoVenda';
import type { PagamentoAplicado } from './saldoPagamento';

/**
 * @param pagamento Forma já aplicada (ou a candidata projetada como se fosse).
 *
 * Campos TEF/PIX só aparecem quando existem: o contrato os tem como opcionais e
 * mandá-los vazios faria o ERP registrar uma transação que não houve.
 */
export function formaParaRetrato(pagamento: PagamentoAplicado): FormaDePagamentoRetrato {
  const forma: Record<string, unknown> = {
    FormaCodigo: pagamento.formaCodigo,
    FormaMeioPagtoNFe: pagamento.meioPagtoNFe,
    // Fronteira de saída: o ERP recebe `double` em reais, não centavos.
    //
    // O dinheiro vai com o que o cliente **entregou** (`valorRecebido`), e as
    // demais formas com o aplicado (`valorRecebido` é `null`, I3). O contrato não
    // tem campo de troco: o ERP o deriva de `Σ FormaValor − total da nota`, então
    // `Σ FormaValor` é o total líquido **mais o troco** (feature 018, AD-284 —
    // revoga D3 da 008). Quem quita a venda continua sendo `valorAplicado`:
    // saldo, faltante e troco exibidos não mudam.
    FormaValor: reaisDeCentavos(pagamento.valorRecebido ?? pagamento.valorAplicado),
    FormaIntegracaoCartao: pagamento.integracaoCartao,
    // `FpgUtiCar` é como o ERP identifica **crediário** ao somar `&TotalCrediario`
    // em `PCheckout_ValidarNFCe` (`erp-validacao-api.md`). Faltava no payload até
    // a feature 014: sem ele o total de crediário fica zero e todo o bloco de
    // limite de crédito é pulado — o gate aprovaria exatamente o caso que existe
    // para barrar. Mesmo modo de falha já documentado para `FormaEntrada`
    // (AD-111), e a mesma correção.
    FormaFpgUtiCar: pagamento.fpgUtiCar,
    // `FR-022`/AD-111: sem `FormaEntrada` o ERP calcula crediário zero.
    FormaEntrada: pagamento.entrada,
    // O campo é por-forma no contrato, não por-venda; string vazia quando não há
    // vale vinculado a esta forma.
    TicketDevolucao: pagamento.ticketDevolucao ?? '',
  };

  // SDT `CheckoutFaturarNFCe` da KB (AD-259, `research.md` D16 da 010):
  // `TEFPagId` é o vínculo com a `TransacaoTEF`, e `PCheckout_FaturarNFCe` lê
  // dela a autorização, o CNPJ do adquirente e a bandeira. Por isso
  // `TEFidentificacao`, `TEFCNPJ` e `TEFNumeroAutorizacao` não saem mais — o SDT
  // não os tem. NSU e autorização de `dadosTEF` são só da tela.
  if (pagamento.dadosTEF !== null) {
    forma.TEFPagId = pagamento.dadosTEF.pagId;
    forma.TEFBandeira = pagamento.dadosTEF.bandeira;
    forma.TEFTipoIntegracao = pagamento.dadosTEF.tipoIntegracao;
  }
  if (pagamento.pixGuid !== null) {
    forma.FormaPixGUID = pagamento.pixGuid;
  }

  return forma;
}
